import { FieldProps, FormControl, useFormField } from '@core/utils/form';
import { TimeInput, toMoment } from '@core/utils/time';
import FormHelperText from '@material-ui/core/FormHelperText';
import moment from 'moment';
import React, { Fragment, useCallback, useMemo } from 'react';
import { FormGroup, Label } from 'reactstrap';
import './form_date.scss';
import CodeMirror from '@uiw/react-codemirror';
import { php } from '@codemirror/lang-php';
import { autocompletion, completeFromList } from '@codemirror/autocomplete';
import { linter, Diagnostic } from '@codemirror/lint';
import { keymap } from '@codemirror/view';
import { defaultKeymap, indentWithTab } from '@codemirror/commands';
import { CodeEditorService } from '@business/services';

export enum CodeEditorLanguageType {
  PHP = 'php',
  SQL = 'sql',
  EXCEL = 'excel',
}

export interface FormCodeEditorProps extends FieldProps<FormControl<string> | FormControl<string | null>> {
  onChange?(code: string | null): void;
  withoutGroup?: boolean;
  asterisk?: boolean;
  clearable?: boolean;
  variables?: { label: string; type: string }[];
  language?: CodeEditorLanguageType;
}

export function FormCodeEditor(props: FormCodeEditorProps) {
  const { formControl, fieldState, fieldConfig } = useFormField(props);

  const fieldTouched = (fieldState && fieldState.touched) || formControl.inheritedSubmitted || false;
  const fieldValue = fieldState.value ?? '';
  const fieldError = (fieldTouched && fieldState.errorMessage) || null;

  const fieldReadonly = fieldConfig.readonly || false;
  const fieldDisabled = fieldConfig.disabled || false;

  const fieldPlaceholder = fieldConfig.placeholder || 'Code...';

  const language = props.language || CodeEditorLanguageType.EXCEL;

  const variables = props.variables || [
    {
      label: '$subtotal',
      type: 'variable',
    },
  ];

  const extensions = useMemo(() => {
    const items = [
      autocompletion({ override: [completeFromList(variables)] }), // Custom variable autocomplete
      linter(view => CodeEditorService.validateLanguage(language, view)),
    ];
    if (language === CodeEditorLanguageType.PHP) {
      items.unshift(php());
    }
    return items;
  }, [variables, language]);

  const content = (
    <Fragment>
      {fieldConfig.label && (
        <Label for={formControl.uid}>
          {fieldConfig.label} {fieldConfig.required && props.asterisk && <span className="text-danger">*</span>}
        </Label>
      )}

      <CodeMirror
        value={fieldValue}
        height="400px"
        extensions={extensions}
        onChange={e => {
          formControl.setValue(e);
          props.onChange && props.onChange(e);
        }}
      />
      {!!fieldError && <FormHelperText style={{ color: '#f44336' }}>{fieldError}</FormHelperText>}
    </Fragment>
  );
  return <FormGroup>{content}</FormGroup>;
}
